import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  const verify = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAdmin(data);
    } catch (err) {
      // 401 just means "not logged in" — expected for regular visitors
      if (err.response?.status !== 401) logError("Session check failed", err);
      setAdmin(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Verify the session cookie actually round-trips before enabling admin UI
    try {
      const { data: me } = await api.get("/auth/me");
      setAdmin(me);
    } catch (err) {
      logError("Cookie verification after login failed", err);
      throw new Error(
        "Signed in, but your browser did not keep the session cookie. Please allow cookies for this site and try again."
      );
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logError("Logout failed", err);
    }
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({ admin, isAdmin: !!admin, checking, login, logout, formatApiError }),
    [admin, checking, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
